/**
 * Núcleo do cadastro por conversa ("Fazer cadastro com a IA").
 * Não usa DOM nem React: só conduz o roteiro e emite eventos para a interface
 * (AiSignupWidget). As falas ficam em TEXTS.
 */
import { isValidPhoneBR } from '../../utils/mask';

export const STEPS = [
  'nome',
  'email',
  'tel',
  'senha',
  'conf',
  'captcha',
  'aceite',
] as const;

export type Step = (typeof STEPS)[number];
export type DocKey = 'priv' | 'termos';

export const DOC_LABELS: Record<DocKey, string> = {
  priv: 'Política de Privacidade',
  termos: 'Termos de Uso',
};

/* Falas do assistente. {variavel} e @@priv@@ / @@termos@@ são trocados na hora. */
export const TEXTS = {
  oi: 'Oi! Vamos criar sua conta. Qual é o seu nome completo?',
  oiDados:
    'Oi, {primeiroNome}! Vamos criar sua conta com os dados que você acabou de enviar.',
  nomeInvalido: 'Preciso do nome e do sobrenome.',
  pedeEmail: 'Prazer, {primeiroNome}! Qual é o seu e-mail?',
  pedeEmailSimples: 'Qual é o seu e-mail?',
  emailInvalido: 'E-mail inválido. Confira e envie de novo.',
  emailExiste:
    'Esse e-mail já tem conta. Entre pelo login ou use outro e-mail.',
  pedeTel: 'Qual é o seu telefone com DDD?',
  telInvalido: 'Telefone inválido. Ex.: (11) 98765-4321',
  pedeSenha: 'Crie uma senha (mínimo 6 caracteres).',
  senhaCurta: 'Mínimo de 6 caracteres.',
  senhaInvalida: 'Essa senha não foi aceita. Crie outra senha.',
  pedeConf: 'Repita a senha.',
  confDiferente: 'As senhas são diferentes. Repita.',
  captcha: 'Quase lá! Quanto é {pergunta}?',
  captchaErro: 'Resposta errada. Quanto é {pergunta}?',
  aceite: 'Para continuar, concorde com a @@priv@@ e os @@termos@@.',
  cadastroOk: 'Conta criada, {primeiroNome}! Entrando…',
  erroRede: 'Não consegui conectar. Tente de novo.',
  erroGenerico: 'Não deu para criar a conta agora. Tente de novo.',
};

type TextKey = keyof typeof TEXTS;

/* Textos da interface (botão, cabeçalho, aceite). */
export const UI = {
  cta: 'Fazer cadastro com a IA',
  ctaContinue: 'Continuar com a IA',
  teaser: 'Sem formulário: é só conversar',
  title: 'Assistente',
  subtitle: 'Cadastro por conversa',
  done: 'Tudo pronto',
  close: 'Fechar conversa',
  send: 'Enviar',
  togglePassword: 'Mostrar ou ocultar a senha',
  consentCheck: 'OK, eu concordo!',
  consentButton: 'Criar minha conta',
  chipLogin: 'Ir para o login',
  back: 'Voltar para a conversa',
};

export interface FieldConfig {
  label: string;
  placeholder: string;
  type: 'text' | 'email' | 'tel' | 'password';
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  autoComplete: string;
  mask?: 'phone';
  secret?: boolean;
}

/* Configuração de cada campo (rótulo, teclado do celular, preenchimento automático). */
export const FIELDS: Partial<Record<Step, FieldConfig>> = {
  nome: {
    label: 'Nome completo',
    placeholder: 'Nome e sobrenome',
    type: 'text',
    inputMode: 'text',
    autoComplete: 'name',
  },
  email: {
    label: 'Seu e-mail',
    placeholder: 'seuemail@exemplo.com',
    type: 'email',
    inputMode: 'email',
    autoComplete: 'email',
  },
  tel: {
    label: 'Telefone com DDD',
    placeholder: '(11) 98765-4321',
    type: 'tel',
    inputMode: 'tel',
    autoComplete: 'tel',
    mask: 'phone',
  },
  senha: {
    label: 'Senha · mínimo 6 caracteres',
    placeholder: 'Crie uma senha',
    type: 'password',
    secret: true,
    autoComplete: 'new-password',
  },
  conf: {
    label: 'Confirme a senha',
    placeholder: 'Repita a senha',
    type: 'password',
    secret: true,
    autoComplete: 'new-password',
  },
  captcha: {
    label: 'Resultado da conta',
    placeholder: 'Qual o resultado?',
    type: 'text',
    inputMode: 'numeric',
    autoComplete: 'off',
  },
};

/* ---------- Contrato com a API ---------- */
export interface Captcha {
  id: string;
  pergunta: string;
}

export interface SignupPayload {
  name: string;
  email: string;
  phoneNumber: string;
  password: string;
}

/** Dados já conhecidos (ex.: do lead). Os válidos não são perguntados de novo. */
export interface SignupPrefill {
  name?: string;
  email?: string;
  phoneNumber?: string;
}

/** Códigos de erro que o fluxo sabe tratar. Qualquer outro vira erro genérico. */
export type RegisterErrorCode = 'EMAIL_EXISTS' | 'INVALID_PASSWORD';

export class RegisterError extends Error {
  code?: RegisterErrorCode;

  constructor(message: string, code?: RegisterErrorCode) {
    super(message);
    this.code = code;
  }
}

export interface SignupApi {
  getCaptcha: () => Promise<Captcha>;
  verifyCaptcha: (body: { id: string; resposta: string }) => Promise<boolean>;
  /** Cria a conta. Em erro conhecido, lança RegisterError com `code`. */
  register: (payload: SignupPayload) => Promise<unknown>;
}

/* ---------- Validação ---------- */
export const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
export const isFullName = (v: string) =>
  v.trim().split(/\s+/).filter(Boolean).length >= 2;
export const firstName = (n: string) => (n || '').trim().split(/\s+/)[0] || '';

/* ---------- Texto ---------- */
export type TextPart =
  | { type: 'text'; value: string }
  | { type: 'doc'; doc: DocKey; label: string };

/** Quebra a fala em partes de texto e links dos documentos. Sem HTML, sem XSS. */
export function parseText(
  text: string,
  vars: Record<string, string> = {}
): TextPart[] {
  const filled = text.replace(/\{(\w+)\}/g, (m, k) =>
    vars[k] !== undefined ? String(vars[k]) : m
  );
  const parts: TextPart[] = [];
  const re = /@@(priv|termos)@@/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(filled))) {
    if (m.index > last)
      parts.push({ type: 'text', value: filled.slice(last, m.index) });
    const doc = m[1] as DocKey;
    parts.push({ type: 'doc', doc, label: DOC_LABELS[doc] });
    last = re.lastIndex;
  }
  if (last < filled.length)
    parts.push({ type: 'text', value: filled.slice(last) });
  return parts;
}

/* ---------- Cor primária (vinda do administrador) ---------- */
const DEFAULT_COLOR = '#F39200';

function normalizeHex(hex: string | undefined): string {
  const h = String(hex || '')
    .trim()
    .replace('#', '');
  if (/^[0-9a-f]{3}$/i.test(h))
    return (
      '#' +
      h
        .split('')
        .map((c) => c + c)
        .join('')
    );
  if (/^[0-9a-f]{6}$/i.test(h)) return '#' + h;
  return DEFAULT_COLOR;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function luminance(hex: string) {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function darken(hex: string, p: number) {
  return (
    '#' +
    hexToRgb(hex)
      .map((v) =>
        Math.round(v * (1 - p))
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
  );
}

/** Deriva as cores do widget a partir da cor primária. Texto branco, exceto em cores muito claras. */
export function colorTokens(color: string | undefined) {
  const hex = normalizeHex(color);
  const L = luminance(hex);
  return {
    primary: hex,
    primaryDark: darken(hex, 0.45),
    onPrimary: L > 0.6 ? '#1b1b1b' : '#ffffff',
    primaryText: L > 0.45 ? darken(hex, 0.45) : hex,
  };
}

/* ---------- Motor da conversa ---------- */
export interface AskEvent {
  type: 'ask';
  step: Step;
  field: FieldConfig | null;
  index: number;
  total: number;
  chips: { id: 'login'; label: string }[];
}

export type FlowEvent =
  | { type: 'reset' }
  | { type: 'busy'; busy: boolean }
  | { type: 'typing'; on: boolean }
  | { type: 'bot'; parts: TextPart[] }
  | { type: 'user'; text: string }
  | AskEvent
  | { type: 'consent'; show: boolean }
  | { type: 'done' }
  | { type: 'goLogin' };

export interface FlowConfig {
  reduceMotion?: boolean;
  onSuccess?: () => void;
  onLogin?: () => void;
}

interface FlowOptions {
  emit: (e: FlowEvent) => void;
  api: () => SignupApi;
  config: () => FlowConfig;
}

interface FlowState {
  step: Step | 'idle';
  data: Partial<{ nome: string; email: string; tel: string; senha: string }>;
  cap: Captcha | null;
  captchaOk: boolean;
  busy: boolean;
  exists: boolean;
  done: boolean;
  started: boolean;
}

export function createSignupFlow({ emit, api, config }: FlowOptions) {
  let run = 0;
  let st: FlowState = fresh();

  function fresh(): FlowState {
    return {
      step: 'idle',
      data: {},
      cap: null,
      captchaOk: false,
      busy: false,
      exists: false,
      done: false,
      started: false,
    };
  }

  const wait = (ms: number) =>
    new Promise((r) => setTimeout(r, config().reduceMotion ? 0 : ms));
  const setBusy = (b: boolean) => {
    st.busy = b;
    emit({ type: 'busy', busy: b });
  };

  /** O assistente "digita" e depois fala. Retorna false se a conversa foi reiniciada no meio. */
  async function say(key: TextKey, vars?: Record<string, string>) {
    const id = run;
    setBusy(true);
    emit({ type: 'typing', on: true });
    await wait(Math.min(700, 300 + TEXTS[key].length * 6));
    if (id !== run) return false;
    emit({ type: 'typing', on: false });
    emit({ type: 'bot', parts: parseText(TEXTS[key], vars) });
    setBusy(false);
    return true;
  }

  /** Chamada à API com indicador de "digitando". */
  async function call<T>(fn: () => Promise<T>) {
    const id = run;
    setBusy(true);
    emit({ type: 'typing', on: true });
    try {
      const value = await fn();
      return { ok: true as const, value, stale: id !== run };
    } catch (error) {
      return { ok: false as const, error, stale: id !== run };
    } finally {
      if (id === run) emit({ type: 'typing', on: false });
    }
  }

  function ask(step: Step) {
    st.step = step;
    setBusy(false);
    emit({
      type: 'ask',
      step,
      field: FIELDS[step] || null,
      index: STEPS.indexOf(step),
      total: STEPS.length,
      chips:
        step === 'email' && st.exists
          ? [{ id: 'login', label: UI.chipLogin }]
          : [],
    });
  }

  async function nextCaptcha(key: TextKey, fallbackStep: Step) {
    const r = await call(() => api().getCaptcha());
    if (r.stale) return;
    if (!r.ok || !r.value) {
      if (await say('erroRede')) ask(fallbackStep);
      return;
    }
    st.cap = r.value;
    if (await say(key, { pergunta: r.value.pergunta })) ask('captcha');
  }

  /**
   * Pergunta o próximo dado que ainda falta depois de `after`.
   * `greeted`: o nome já foi saudado, então o e-mail é pedido sem o "Prazer".
   */
  async function askNext(after: 'nome' | 'email' | 'tel', greeted = false) {
    const order = ['nome', 'email', 'tel', 'senha'] as const;
    const next = order
      .slice(order.indexOf(after) + 1)
      .find((step) => step === 'senha' || !st.data[step]);
    if (next === 'email') {
      const ok = greeted
        ? await say('pedeEmailSimples')
        : await say('pedeEmail', { primeiroNome: firstName(st.data.nome) });
      if (ok) ask('email');
      return;
    }
    if (next === 'tel') {
      if (await say('pedeTel')) ask('tel');
      return;
    }
    if (await say('pedeSenha')) ask('senha');
  }

  async function showConsent() {
    if (await say('aceite')) {
      ask('aceite');
      emit({ type: 'consent', show: true });
    }
  }

  const handlers: Partial<Record<Step, (v: string) => Promise<void>>> = {
    async nome(v) {
      emit({ type: 'user', text: v });
      if (!isFullName(v)) {
        if (await say('nomeInvalido')) ask('nome');
        return;
      }
      st.data.nome = v;
      await askNext('nome');
    },
    async email(v) {
      emit({ type: 'user', text: v });
      st.exists = false;
      if (!isEmail(v)) {
        if (await say('emailInvalido')) ask('email');
        return;
      }
      st.data.email = v.toLowerCase();
      // Voltou por e-mail já cadastrado: o resto já foi respondido.
      if (st.captchaOk) {
        await showConsent();
        return;
      }
      await askNext('email');
    },
    async tel(v) {
      emit({ type: 'user', text: v });
      if (!isValidPhoneBR(v)) {
        if (await say('telInvalido')) ask('tel');
        return;
      }
      st.data.tel = v;
      await askNext('tel');
    },
    async senha(v) {
      emit({ type: 'user', text: '•'.repeat(v.length) });
      if (v.length < 6) {
        if (await say('senhaCurta')) ask('senha');
        return;
      }
      st.data.senha = v;
      if (await say('pedeConf')) ask('conf');
    },
    async conf(v) {
      emit({ type: 'user', text: '•'.repeat(v.length) });
      if (v !== st.data.senha) {
        if (await say('confDiferente')) ask('conf');
        return;
      }
      // Voltou por senha recusada: o captcha já foi resolvido.
      if (st.captchaOk) {
        await showConsent();
        return;
      }
      await nextCaptcha('captcha', 'conf');
    },
    async captcha(v) {
      emit({ type: 'user', text: v });
      const cap = st.cap;
      const r = await call(() =>
        api().verifyCaptcha({ id: cap.id, resposta: v })
      );
      if (r.stale) return;
      if (!r.ok) {
        if (await say('erroRede')) ask('captcha');
        return;
      }
      if (!r.value) {
        await nextCaptcha('captchaErro', 'captcha');
        return;
      }
      st.captchaOk = true;
      await showConsent();
    },
  };

  /** Envia a resposta da pessoa para o passo atual. */
  async function submit(value: string) {
    if (st.busy || st.done || st.step === 'idle') return;
    const handler = handlers[st.step];
    if (!handler) return;
    const field = FIELDS[st.step];
    const raw = String(value == null ? '' : value);
    const v = field && field.secret ? raw : raw.trim(); // senha nunca é "aparada"
    if (!v) return;
    await handler(v);
  }

  /** A pessoa marcou "OK, eu concordo!" e tocou em "Criar minha conta". */
  async function acceptConsent() {
    if (st.step !== 'aceite' || st.busy || st.done) return;
    const id = run;
    emit({ type: 'consent', show: false });
    emit({ type: 'user', text: UI.consentCheck });
    const r = await call(() =>
      api().register({
        name: st.data.nome,
        email: st.data.email,
        phoneNumber: st.data.tel,
        password: st.data.senha,
      })
    );
    if (r.stale) return;
    if (r.ok === false) {
      const code = (r.error as RegisterError)?.code;
      if (code === 'EMAIL_EXISTS') {
        st.exists = true;
        if (await say('emailExiste')) ask('email');
        return;
      }
      if (code === 'INVALID_PASSWORD') {
        if (await say('senhaInvalida')) ask('senha');
        return;
      }
      if (await say('erroGenerico')) {
        ask('aceite');
        emit({ type: 'consent', show: true });
      }
      return;
    }
    st.done = true;
    if (!(await say('cadastroOk', { primeiroNome: firstName(st.data.nome) })))
      return;
    emit({ type: 'done' });
    await wait(900);
    if (id !== run) return;
    try {
      config().onSuccess?.();
    } catch (_) {
      /* a página decide o destino */
    }
  }

  /** Atalhos exibidos ao lado do campo (hoje: "Ir para o login"). */
  function chip(chipId: 'login') {
    if (chipId === 'login') {
      emit({ type: 'goLogin' });
      try {
        config().onLogin?.();
      } catch (_) {
        /* idem */
      }
    }
  }

  async function start(prefill?: SignupPrefill) {
    if (st.started) return;
    st.started = true;

    // Só aproveita o que passaria na validação do próprio chat.
    const name = prefill?.name?.trim().replace(/\s+/g, ' ');
    const email = prefill?.email?.trim().toLowerCase();
    const tel = prefill?.phoneNumber?.trim();
    if (name && isFullName(name)) st.data.nome = name;
    if (email && isEmail(email)) st.data.email = email;
    if (tel && isValidPhoneBR(tel)) st.data.tel = tel;

    if (!st.data.nome) {
      if (await say('oi')) ask('nome');
      return;
    }
    if (!(await say('oiDados', { primeiroNome: firstName(st.data.nome) })))
      return;
    await askNext('nome', true);
  }

  function reset() {
    run += 1;
    st = fresh();
    emit({ type: 'reset' });
  }

  return { start, submit, acceptConsent, chip, reset };
}

export type SignupFlow = ReturnType<typeof createSignupFlow>;
